import React, { useEffect, useState, useRef } from 'react'
import { Combined1500GeneralSummary } from 'src/viewModels/workflow/Combined1500GeneralSummary'
import {
  AccordionSummary,
  Button,
  Checkbox,
  CircularProgress,
  Chip,
  Grid,
  IconButton,
  List,
  ListItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import Moment from 'moment'
import { formatCurrency } from '../utils/Formatter'
import ReactPaginate from 'react-paginate'
import CardWithNoTitle from '../common/CardWithNoTitle/CardWithNoTitle'
import {
  PlaylistAdd,
  ArrowDropUp,
  ArrowDropDown,
  LocalAtmRounded,
  ReceiptOutlined,
} from '@mui/icons-material'
import { PlaylistRemove } from 'mdi-material-ui'
import {
  Combined1500ComponentDetail,
  ICombined1500ComponentDetail,
} from '../viewModels/workflow/Combined1500ComponentDetail'
import { Form, Formik } from 'formik'
import * as yup from 'yup'
import { ICombined1500ComponentLineItem } from '../viewModels/workflow/Combined1500ComponentLineItem'
import FullWidthField from '../common/FullWidthField'
import './CreateCombined1500.css'
import { Helmet } from 'react-helmet'
import DatePickerField from '../common/DatePickerField/DatePickerField'
import { Combined1500Request } from '../viewModels/workflow/Combined1500Request'
import createPdfObjUrl from '../utils/CreatePdfObjUrl'
import { previewCombined1500 } from '../services/BillOutService'
import {
  GetCombined1500ComponentDetail,
  GetCombined1500GeneralSummary,
  GetCombined1500ClaimFieldSummaryAsync,
  processAction,
} from '../services/WorkflowService'
import { ObjectiveProcessAction } from '../viewModels/workflow/ObjectiveProcessAction'
import queryString from 'query-string'
import BillOutHistoryModal from './BillOutHistoryModal'
import SortedTableCell, { sortedTableHeaderSx } from 'src/workflow/SortedTableCell'
import { DocumentButtonSummaryResult } from 'src/viewModels/quotes/DocumentButtonSummaryResult'
import { GetClaimButtonsWithSummary } from 'src/services/QuoteService'
import QuoteClaimButton from './QuoteClaimButton'
import InjectedActionLogModal from 'src/workflow/modals/shared/ActionLogModal'
import { InboxItemType } from 'src/utils/InboxItemType'

interface IStepDetail {
  name?: string | undefined
  providerTypeAbbreviation: string | undefined
}

class StepDetail implements IStepDetail {
  name?: string | undefined
  providerTypeAbbreviation: string | undefined
}

interface IWizardState {
  selectedDxCodes: string[]
  selectedCms1500Id: string | null
  dateOfInjury: Date | null
  box14Qualifier: string | null
  otherDate: Date | null
  box15Qualifier: string | null
  referringDoctor: string | null
  referringDoctorNpi: string | null
  box30: number | null
  claimsGridState: IGridState
  combined1500ComponentDetails: ICombined1500ComponentDetail[]
  claimButtonsByType: { [type: string]: DocumentButtonSummaryResult[] }
  lineItemSort: string[]
  cptCode?: string
  providerTypeId?: string
  providerId?: string
  sortColumn?: string
  sortOrder?: 'asc' | 'desc'
}

class WizardState implements IWizardState {
  selectedDxCodes: string[] = new Array<string>()
  selectedCms1500Id!: string | null
  dateOfInjury!: Date | null
  box14Qualifier!: string | null
  otherDate!: Date | null
  box15Qualifier!: string | null
  referringDoctor!: string | null
  referringDoctorNpi!: string | null
  box30!: number | null
  claimsGridState: IGridState = new GridState()
  combined1500ComponentDetails: ICombined1500ComponentDetail[] =
    new Array<ICombined1500ComponentDetail>()
  claimButtonsByType: { [type: string]: DocumentButtonSummaryResult[] } = {}
  lineItemSort: string[] = new Array<string>()
  cptCode?: string
  providerTypeId?: string
  providerId?: string
}

interface IGridState {
  page: number
  pageCount: number
  firstRecordIndex: number
  lastRecordIndex: number
  totalItems: number
  pageSize: number
}

class GridState implements IGridState {
  page: number = 1
  pageCount: number = 0
  firstRecordIndex: number = 0
  lastRecordIndex: number = 0
  totalItems: number = 0
  pageSize: number = 25
}

let generalInformationSchema = yup.object({
  selectedDxCodes: yup.array().min(1, 'At least one DX Code is required'),
  selectedCms1500Id: yup.string().required(),
  dateOfInjury: yup.date(),
  otherDate: yup.date(),
  referringDoctor: yup.string(),
  referringDoctorNpi: yup.string(),
  box30: yup
    .number()
    .nullable()
    .transform((_, originalValue) => {
      return originalValue === '' ? null : Number(String(originalValue).replace(/,/g, ''))
    }),
})

let componentSchema = yup.object({
  providerTypeAbbreviation: yup.string().required(),
  invoiceUsualAndCustomaryTotal: yup.number().required(),
  lineItems: yup
    .array()
    .required()
    .of(
      yup.object().shape({
        id: yup.string(),
        lineNumber: yup.number(),
        charges: yup.number(),
        cpt: yup.string(),
        dateOfService: yup.date(),
        isExcluded: yup.boolean().required(),
        modifier1: yup.string(),
        modifier2: yup.string(),
        modifier3: yup.string(),
        modifier4: yup.string(),
        npi: yup.string().when('isExcluded', {
          is: true,
          then: (schema: any) => schema.notRequired(),
          otherwise: (schema: any) => schema.required('Required'),
        }),
        placeOfService: yup.string(),
        providerTypeAbbreviation: yup.string(),
        providerTypeId: yup.string(),
        providerId: yup.string(),
        quantity: yup.string(),
        diagnosisPointer: yup.string().when('isExcluded', {
          is: true,
          then: (schema: any) => schema.notRequired(),
          otherwise: (schema: any) => schema.required('Required'),
        }),
        usualAndCustomary: yup
          .number()
          .nullable()
          .transform((_, originalValue) => {
            return originalValue === ''
              ? null
              : Number(String(originalValue).replace(/,/g, ''))
          })
          .when('isExcluded', {
            is: true,
            then: (schema: any) => schema.notRequired(),
            otherwise: (schema: any) => schema.required('Required'),
          }),
        status: yup.string(),
        claimGroupNumber: yup.number(),
      })
    ),
})

let schemas: any[] = [generalInformationSchema, componentSchema]

let ref = new Map<string, any>()

function getStepContent(
  stepIndex: number,
  summary: Combined1500GeneralSummary | null,
  wizardState: IWizardState,
  setWizardState: any,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  stepDetails: IStepDetail[],
  pdfContent: any,
  updateModelRef?: React.MutableRefObject<(() => void) | null>,
  formRefObj?: React.MutableRefObject<any | null>
) {
  const step1Content = getStep1Content(
    summary,
    wizardState,
    setWizardState,
    setIsLoading,
    updateModelRef,
    formRefObj
  )
  const pcStepContent = getComponentStepContent(
    summary,
    wizardState,
    setWizardState,
    stepDetails.find((x) => x.providerTypeAbbreviation === 'PC') ?? new StepDetail()
  )
  const tcStepContent = getComponentStepContent(
    summary,
    wizardState,
    setWizardState,
    stepDetails.find((x) => x.providerTypeAbbreviation === 'TC') ?? new StepDetail()
  )
  const anStepContent = getComponentStepContent(
    summary,
    wizardState,
    setWizardState,
    stepDetails.find((x) => x.providerTypeAbbreviation === 'AN') ?? new StepDetail()
  )
  const miStepContent = getComponentStepContent(
    summary,
    wizardState,
    setWizardState,
    stepDetails.find((x) => x.providerTypeAbbreviation === 'MI') ?? new StepDetail()
  )
  const ptStepContent = getComponentStepContent(
    summary,
    wizardState,
    setWizardState,
    stepDetails.find((x) => x.providerTypeAbbreviation === 'PT') ?? new StepDetail()
  )

  const lineReviewStepContent = getLineReviewStepContent(
    summary,
    wizardState,
    setWizardState
  )

  const finalStepContent = getFinalStepContent(summary, pdfContent)

  if (stepIndex == 0) {
    return step1Content
  }
  if (stepIndex == stepDetails.length - 1) {
    return finalStepContent
  }
  if (stepIndex == stepDetails.length - 2) {
    return lineReviewStepContent
  }

  switch (stepDetails[stepIndex].providerTypeAbbreviation) {
    case 'PC':
      return pcStepContent
    case 'TC':
      return tcStepContent
    case 'AN':
      return anStepContent
    case 'MI':
      return miStepContent
    case 'PT':
      return ptStepContent
    default:
      return 'Invalid step'
  }
}

const renderHeader = (summary: Combined1500GeneralSummary | null) => {
  const [openActionLogModal, setOpenActionLogModal] = useState(false)

  const handleCloseHistoryModal = () => {
    setOpenActionLogModal(false)
  }

  return (
    <div style={{ width: '100%' }}>
      <Grid container item spacing={1}>
        <Grid container item direction="row" spacing={3}>
          <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            <span style={{ fontWeight: 'bold' }}>Patient Name: </span>
            <span>{summary?.quoteDetail?.patientName}</span>
          </Grid>
          <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            <span style={{ fontWeight: 'bold' }}>Type: </span>
            <span>{summary?.quoteDetail?.quoteType}</span>
          </Grid>
          <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            <span style={{ fontWeight: 'bold' }}>Start Date: </span>
            <span>
              {summary?.quoteDetail?.startDate &&
                Moment(summary?.quoteDetail?.startDate).format('MM/DD/YYYY')}
            </span>
          </Grid>
          <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            <span style={{ fontWeight: 'bold' }}># of Days: </span>
            <span>{summary?.quoteDetail?.bundleDurationDays}</span>
          </Grid>
          <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            <span style={{ fontWeight: 'bold' }}>End Date: </span>
            <span>
              {summary?.quoteDetail?.endDate &&
                Moment(summary?.quoteDetail?.endDate).format('MM/DD/YYYY')}
            </span>
          </Grid>
          <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            <span style={{ fontWeight: 'bold' }}>Body Part: </span>
            <span>{summary?.quoteDetail?.bodyPart}</span>
          </Grid>
          <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            <span style={{ fontWeight: 'bold' }}>Date of Birth: </span>
            <span>
              {summary?.quoteDetail?.patientDateOfBirth &&
                Moment(summary?.quoteDetail?.patientDateOfBirth).format('MM/DD/YYYY')}
            </span>
          </Grid>
        </Grid>
        <Grid container item direction="row" spacing={3}>
          <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            <span style={{ fontWeight: 'bold' }}>EOC/Legacy: </span>
            <span>{summary?.quoteDetail?.eocNumber}</span>
          </Grid>
          <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            <span style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              Group #:&nbsp;
              {summary?.quoteDetail?.groupNumber}
              &nbsp;&nbsp;
              <Tooltip title="Show Workflow Action Log">
                <ReceiptOutlined
                  style={{ cursor: 'pointer' }}
                  fontSize="small"
                  onClick={() => {
                    setOpenActionLogModal(true)
                  }}
                />
              </Tooltip>
            </span>
          </Grid>
          <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            <span style={{ fontWeight: 'bold' }}>Ins. Company: </span>
            <span>{summary?.quoteDetail?.insuranceCompanyName}</span>
          </Grid>
          <Grid item sm={1} style={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            {summary?.quoteDetail?.isConsumer && (
              <Chip
                label="CONSUMER QUOTE"
                size="small"
                variant="outlined"
                icon={
                  <span
                    style={{
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    🦈
                  </span>
                }
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(128, 128, 128, 0.2)',
                  color: (theme) =>
                    theme.palette.mode === 'dark'
                      ? theme.palette.common.white
                      : theme.palette.primary.main,
                  borderColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[500]
                      : 'rgba(128, 128, 128, 0.2)',
                  height: 'auto',
                }}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
      {summary && summary?.quoteId !== '' && (
        <InjectedActionLogModal
          inboxType={InboxItemType.Quote}
          isOpen={openActionLogModal}
          associatedItemId={summary?.quoteId.toString() ?? ''}
          closeModal={handleCloseHistoryModal}
        />
      )}
    </div>
  )
}

function renderDxCodes(wizardState: IWizardState) {
  return (
    <>
      <div style={{ width: '100%', marginTop: '22px', marginBottom: '11px' }}>
        <AccordionSummary>
          <Grid container justifyContent="space-between" style={{ paddingTop: '5px' }}>
            <Typography
              display="inline"
              color="textSecondary"
              variant="body2"
              component="div"
            >
              SELECTED DX CODES
            </Typography>
            <Typography
              display="inline"
              color="textPrimary"
              variant="subtitle1"
              align="right"
              component="div"
            >
              <span></span>
            </Typography>
          </Grid>
        </AccordionSummary>
      </div>
      <div style={{ width: '100%' }}>
        <div>
          <Table style={{ width: '100%' }}>
            <TableBody>
              <TableRow>
                <TableCell>
                  <TextField
                    fullWidth
                    variant="standard"
                    InputProps={{ readOnly: true }}
                    label="A"
                    defaultValue={wizardState.selectedDxCodes[0] ?? ' '}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    variant="standard"
                    InputProps={{ readOnly: true }}
                    label="B"
                    defaultValue={wizardState.selectedDxCodes[1] ?? ' '}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    variant="standard"
                    InputProps={{ readOnly: true }}
                    label="C"
                    defaultValue={wizardState.selectedDxCodes[2] ?? ' '}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    variant="standard"
                    InputProps={{ readOnly: true }}
                    label="D"
                    defaultValue={wizardState.selectedDxCodes[3] ?? ' '}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <TextField
                    fullWidth
                    variant="standard"
                    InputProps={{ readOnly: true }}
                    label="E"
                    defaultValue={wizardState.selectedDxCodes[4] ?? ' '}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    variant="standard"
                    InputProps={{ readOnly: true }}
                    label="F"
                    defaultValue={wizardState.selectedDxCodes[5] ?? ' '}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    variant="standard"
                    InputProps={{ readOnly: true }}
                    label="G"
                    defaultValue={wizardState.selectedDxCodes[6] ?? ' '}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    variant="standard"
                    InputProps={{ readOnly: true }}
                    label="H"
                    defaultValue={wizardState.selectedDxCodes[7] ?? ' '}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <TextField
                    fullWidth
                    variant="standard"
                    InputProps={{ readOnly: true }}
                    label="I"
                    defaultValue={wizardState.selectedDxCodes[8] ?? ' '}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    variant="standard"
                    InputProps={{ readOnly: true }}
                    label="J"
                    defaultValue={wizardState.selectedDxCodes[9] ?? ' '}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    variant="standard"
                    InputProps={{ readOnly: true }}
                    label="K"
                    defaultValue={wizardState.selectedDxCodes[10] ?? ' '}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    variant="standard"
                    InputProps={{ readOnly: true }}
                    label="L"
                    defaultValue={wizardState.selectedDxCodes[11] ?? ' '}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  )
}

function getLineReviewStepContent(
  summary: Combined1500GeneralSummary | null,
  wizardState: IWizardState,
  setWizardState: any
) {
  let includedLineItems = wizardState.combined1500ComponentDetails.flatMap((x) =>
    x.lineItems.filter((y) => !y.isExcluded)
  )
  let lineItems = wizardState.lineItemSort.map((x) =>
    includedLineItems.find((y) => x === y.id)
  )

  const moveUp = (id: string) => {
    let lineItemSort = wizardState.lineItemSort
    let index: number = lineItemSort.findIndex((x) => x === id)
    if (index > 0) {
      let temp = lineItemSort[index]
      lineItemSort[index] = lineItemSort[index - 1]
      lineItemSort[index - 1] = temp
      setWizardState({
        ...wizardState,
        lineItemSort,
      })
    }
  }

  const moveDown = (id: string) => {
    let lineItemSort = wizardState.lineItemSort
    let index: number = wizardState.lineItemSort.findIndex((x) => x === id)
    if (index < wizardState.lineItemSort.length - 1) {
      let temp = wizardState.lineItemSort[index]
      wizardState.lineItemSort[index] = wizardState.lineItemSort[index + 1]
      wizardState.lineItemSort[index + 1] = temp
      setWizardState({
        ...wizardState,
        lineItemSort,
      })
    }
  }

  const sort = (columnName: string) => {
    const propertyNameMap: { [key: string]: string } = {
      'Date of Service': 'dateOfService',
      'Group #': 'claimGroupNumber',
      'Provider Type': 'providerTypeAbbreviation',
      Status: 'status',
      'Place of Service': 'placeOfService',
      CPT: 'cpt',
      Charges: 'charges',
      'Usual and Customary': 'usualAndCustomary',
      Qty: 'quantity',
      NPI: 'npi',
      'Diagnosis Pointer': 'diagnosisPointer',
      'Client Assistance Field': 'jRendering',
    }

    const propertyName = propertyNameMap[columnName] || columnName

    const newSortOrder: 'asc' | 'desc' =
      wizardState.sortColumn === columnName && wizardState.sortOrder === 'asc'
        ? 'desc'
        : 'asc'

    const sortedLineItemIds = [...wizardState.lineItemSort].sort((id1, id2) => {
      const item1 = includedLineItems.find((x) => x.id === id1)
      const item2 = includedLineItems.find((x) => x.id === id2)

      if (!item1 || !item2) return 0

      let value1: any = (item1 as any)[propertyName]
      let value2: any = (item2 as any)[propertyName]

      if (propertyName === 'dateOfService') {
        value1 = new Date(value1).getTime()
        value2 = new Date(value2).getTime()
      }

      if (
        ['charges', 'usualAndCustomary', 'quantity', 'jRendering'].includes(propertyName)
      ) {
        const toNumber = (value: any) =>
          value === undefined || value === null || value === '' ? NaN : Number(value)
        value1 = toNumber(value1)
        value2 = toNumber(value2)

        if (isNaN(value1) || isNaN(value2)) {
          return isNaN(value1) === isNaN(value2) ? 0 : isNaN(value1) ? 1 : -1
        }
      }

      if (typeof value1 === 'number' && typeof value2 === 'number') {
        return newSortOrder === 'asc' ? value1 - value2 : value2 - value1
      }

      if (typeof value1 === 'string' && typeof value2 === 'string') {
        const comparison = value1.localeCompare(value2)
        return newSortOrder === 'asc' ? comparison : -comparison
      }

      return 0
    })

    setWizardState({
      ...wizardState,
      sortColumn: columnName,
      sortOrder: newSortOrder,
      lineItemSort: sortedLineItemIds,
    })
  }

  const renderSortedHeader = (
    columnName: string,
    propertyName: string,
    align?: any,
    style?: React.CSSProperties
  ) => (
    <SortedTableCell
      columnName={columnName}
      propertyName={propertyName}
      align={align}
      style={style}
      order={wizardState.sortOrder ?? 'asc'}
      orderBy={wizardState.sortColumn ?? ''}
      onClick={sort}
      sortable={lineItems.length > 0}
      reserveSortIconSpace
    />
  )

  return (
    <CardWithNoTitle>
      {renderHeader(summary)}
      <div style={{ width: '100%', marginTop: '22px', marginBottom: '11px' }}>
        <AccordionSummary>
          <Grid container justifyContent="space-between" style={{ paddingTop: '5px' }}>
            <Typography
              display="inline"
              color="textSecondary"
              variant="body2"
              className="componentSectionHeader"
              component="div"
            >
              Line Items
            </Typography>
          </Grid>
        </AccordionSummary>
      </div>
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <Table
          sx={{
            ...sortedTableHeaderSx,
            width: '100%',
            '& tbody td': {
              paddingLeft: '3px',
              paddingRight: '3px',
              whiteSpace: 'nowrap',
            },
          }}
        >
          <TableHead>
            <TableRow>
              {renderSortedHeader('Date of Service', 'Date of Service', 'right', {
                whiteSpace: 'nowrap',
                paddingLeft: '3px',
                paddingRight: '3px',
              })}
              {renderSortedHeader('Group #', 'Group #', 'right', {
                whiteSpace: 'nowrap',
                paddingLeft: '3px',
                paddingRight: '3px',
              })}
              {renderSortedHeader('Provider Type', 'Provider Type', 'left', {
                whiteSpace: 'nowrap',
                paddingLeft: '3px',
                paddingRight: '3px',
              })}
              {renderSortedHeader('Status', 'Status', 'left', {
                whiteSpace: 'nowrap',
                paddingLeft: '3px',
                paddingRight: '3px',
              })}
              {renderSortedHeader('Place of Service', 'Place of Service', 'left', {
                whiteSpace: 'nowrap',
                paddingLeft: '3px',
                paddingRight: '3px',
              })}
              {renderSortedHeader('CPT', 'CPT', 'left', {
                whiteSpace: 'nowrap',
                paddingLeft: '3px',
                paddingRight: '3px',
              })}
              <SortedTableCell
                columnName="Modifiers"
                propertyName="Modifiers"
                colSpan={4}
                sortable={false}
                style={{ whiteSpace: 'nowrap', paddingLeft: '3px', paddingRight: '3px' }}
                order={wizardState.sortOrder ?? 'asc'}
                orderBy={wizardState.sortColumn ?? ''}
                onClick={sort}
              />
              {renderSortedHeader('Diagnosis Pointer', 'Diagnosis Pointer', 'left', {
                whiteSpace: 'nowrap',
                paddingLeft: '3px',
                paddingRight: '3px',
              })}
              {renderSortedHeader('Charges', 'Charges', 'right', {
                whiteSpace: 'nowrap',
                paddingLeft: '3px',
                paddingRight: '3px',
              })}
              {renderSortedHeader('Usual and Customary', 'Usual and Customary', 'right', {
                whiteSpace: 'nowrap',
                paddingLeft: '3px',
                paddingRight: '3px',
              })}
              {renderSortedHeader('Qty', 'Qty', 'right', {
                whiteSpace: 'nowrap',
                paddingLeft: '3px',
                paddingRight: '3px',
              })}
              {renderSortedHeader('NPI', 'NPI', 'left', {
                whiteSpace: 'nowrap',
                paddingLeft: '3px',
                paddingRight: '3px',
              })}
              {renderSortedHeader(
                'Client Assistance Field',
                'Client Assistance Field',
                'right',
                { whiteSpace: 'nowrap', paddingLeft: '3px', paddingRight: '3px' }
              )}
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lineItems.length > 0 &&
              includedLineItems.length === wizardState.lineItemSort.length &&
              lineItems
                .filter(
                  (item): item is ICombined1500ComponentLineItem => item !== undefined
                )
                .map((lineItem, index) => {
                  return (
                    <TableRow hover key={lineItem.id || index}>
                      <TableCell align="right">
                        <span>
                          {lineItem.dateOfService &&
                            Moment(lineItem.dateOfService).format('MM/DD/YYYY')}
                        </span>
                      </TableCell>
                      <TableCell align="right">{lineItem.claimGroupNumber}</TableCell>
                      <TableCell>{lineItem.providerTypeAbbreviation}</TableCell>
                      <TableCell>{lineItem.status}</TableCell>
                      <TableCell>{lineItem.placeOfService}</TableCell>
                      <TableCell>{lineItem.cpt}</TableCell>
                      <TableCell width={'80px'}>{lineItem.modifier1}</TableCell>
                      <TableCell width={'80px'}>{lineItem.modifier2}</TableCell>
                      <TableCell width={'80px'}>{lineItem.modifier3}</TableCell>
                      <TableCell width={'80px'}>{lineItem.modifier4}</TableCell>
                      <TableCell>{lineItem.diagnosisPointer}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(lineItem.charges.toString())}
                      </TableCell>
                      <TableCell align="right">
                        {lineItem.usualAndCustomary &&
                          formatCurrency(lineItem.usualAndCustomary.toString())}
                      </TableCell>
                      <TableCell align="right">{lineItem.quantity}</TableCell>
                      <TableCell>{lineItem.npi}</TableCell>
                      <TableCell align="right">
                        {lineItem.jRendering &&
                          formatCurrency(lineItem.jRendering.toString())}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          disabled={index === 0}
                          size="small"
                          onClick={() => {
                            moveUp(lineItem.id)
                          }}
                        >
                          <ArrowDropUp fontSize="large" />
                        </IconButton>
                        <IconButton
                          color="primary"
                          disabled={index === lineItems.length - 1}
                          size="small"
                          onClick={() => {
                            moveDown(lineItem.id)
                          }}
                        >
                          <ArrowDropDown fontSize="large" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })}
          </TableBody>
        </Table>
      </div>
    </CardWithNoTitle>
  )
}

function getComponentStepContent(
  summary: Combined1500GeneralSummary | null,
  wizardState: IWizardState,
  setWizardState: any,
  stepDetail: IStepDetail
) {
  const [uAndCSum, setUAndCSum] = useState(0)
  const [uAndCCount, setUAndCCount] = useState(0)
  const [isBillOutHistoryModalOpen, setisBillOutHistoryModalOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined)
  const [selectedLineItem, setSelectedLineItem] =
    useState<ICombined1500ComponentLineItem | null>(null)
  const [setFieldValueRef, setSetFieldValueRef] = useState<
    (field: string, value: any) => void
  >(() => () => {})

  const handleOpenModal = (index: number) => {
    setSelectedIndex(index)
    if (val && val.lineItems) {
      setSelectedLineItem(val.lineItems[index])
    }
    setisBillOutHistoryModalOpen(true)
  }

  const handleCloseModal = () => {
    setisBillOutHistoryModalOpen(false)
    setSelectedIndex(undefined)
    setSelectedLineItem(null)
  }

  let val = wizardState.combined1500ComponentDetails.find(
    (x) => x.providerTypeAbbreviation == stepDetail.providerTypeAbbreviation
  )

  let updateModel = () => {
    let gridReference = ref.get(stepDetail.providerTypeAbbreviation ?? '')
    if (!gridReference) {
      return
    }
    let componentDetails: ICombined1500ComponentDetail[] = [
      ...wizardState.combined1500ComponentDetails,
    ]
    let arrayIndex = componentDetails.findIndex(
      (x) => x.providerTypeAbbreviation == stepDetail.providerTypeAbbreviation
    )

    // Preserve invoiceUsualAndCustomaryTotal from the existing component detail
    let existingDetail = componentDetails[arrayIndex]
    let newComponentDetail = {
      ...gridReference.values,
      invoiceUsualAndCustomaryTotal: existingDetail?.invoiceUsualAndCustomaryTotal ?? 0,
    }

    componentDetails[arrayIndex] = newComponentDetail

    setWizardState({
      ...wizardState,
      combined1500ComponentDetails: componentDetails,
    })

    setTimeout(() => {
      let uAndCValues = newComponentDetail?.lineItems
        ?.filter((x: any) => x && !x.isExcluded)
        .map((x: any) => parseFloat((x.usualAndCustomary ?? 0) as unknown as string))
        .filter((x: any) => x)
      let sum = uAndCValues
        ? (uAndCValues.reduce((a: any, b: any) => (a ?? 0) + (b ?? 0), 0) ?? 0)
        : 0
      setUAndCSum(sum)
      setUAndCCount(uAndCValues ? uAndCValues.length : 0)
    })
  }

  const onBlur: any = () => {
    updateModel()
  }

  const handleCopyChargesToUC = () => {
    if (!val || !val.lineItems) return
    val.lineItems.forEach((item: ICombined1500ComponentLineItem, index: number) => {
      setFieldValueRef(`lineItems.${index}.usualAndCustomary`, item.charges)
    })

    setTimeout(() => {
      updateModel()
    })
  }

  const zeroOutRemaining = () => {
    if (!val || !val.lineItems) return
    val.lineItems.forEach((_item: ICombined1500ComponentLineItem, index: number) => {
      // Read directly from DOM to get the current actual value
      const inputElement = document.getElementById(
        `lineItems[${index}].usualAndCustomary`
      ) as HTMLInputElement
      if (inputElement) {
        const domValue = inputElement.value.trim()
        if (domValue === '' || Number.isNaN(Number(domValue))) {
          setFieldValueRef(`lineItems.${index}.usualAndCustomary`, 0)
        }
      }
    })

    setTimeout(() => {
      updateModel()
    })
  }

  const excludeRemaining = () => {
    if (!val || !val.lineItems) return
    val.lineItems.forEach((_item: ICombined1500ComponentLineItem, index: number) => {
      // Read directly from DOM to get the current actual value
      const inputElement = document.getElementById(
        `lineItems[${index}].usualAndCustomary`
      ) as HTMLInputElement
      if (inputElement) {
        const domValue = inputElement.value.trim()
        if (domValue === '' || Number.isNaN(Number(domValue))) {
          setFieldValueRef(`lineItems.${index}.isExcluded`, true)
        }
      }
    })

    setTimeout(() => {
      updateModel()
    })
  }

  const hasBlankUAndC = () => {
    if (!val || !val.lineItems) return false
    return val.lineItems.some((item: ICombined1500ComponentLineItem, index: number) => {
      if (item.isExcluded) return false
      const inputElement = document.getElementById(
        `lineItems[${index}].usualAndCustomary`
      ) as HTMLInputElement
      if (inputElement) {
        const domValue = inputElement.value.trim()
        return domValue === '' || Number.isNaN(Number(domValue))
      }
      return false
    })
  }

  const renderClaimButtons = (type: string): JSX.Element | JSX.Element[] => {
    const buttons = wizardState.claimButtonsByType[type] || []
    return buttons.map((element: DocumentButtonSummaryResult) => (
      <QuoteClaimButton
        key={element.claimId}
        element={element}
        episodeOfCareId={summary?.quoteDetail?.episodeOfCareId}
      />
    ))
  }

  return (
    <CardWithNoTitle>
      {renderHeader(summary)}
      {renderDxCodes(wizardState)}
      <div style={{ width: '100%', marginTop: '22px', marginBottom: '11px' }}>
        <AccordionSummary>
          <Grid container justifyContent="space-between" style={{ paddingTop: '5px' }}>
            <Typography
              display="inline"
              color="textSecondary"
              variant="body2"
              className="componentSectionHeader"
              component="div"
            >
              {stepDetail.name}
            </Typography>
            <Typography
              display="inline"
              color="textPrimary"
              variant="subtitle1"
              align="right"
              component="div"
            >
              <span>
                {` Invoiced U&C Total: ${formatCurrency(
                  (val?.invoiceUsualAndCustomaryTotal ?? 0).toString()
                )} Usual and Customary Total: ${formatCurrency(
                  uAndCSum.toString()
                )} (${uAndCCount} Items)`}
              </span>
            </Typography>
          </Grid>
        </AccordionSummary>
      </div>
      <div style={{ width: '100%' }}>
        {val ? (
          <Formik
            key={stepDetail.providerTypeAbbreviation}
            initialValues={val ?? new Combined1500ComponentDetail()}
            validationSchema={componentSchema}
            onSubmit={() => alert('submit')}
            enableReinitialize={false}
            validateOnChange={true}
            validateOnBlur={true}
            innerRef={(p: any) => {
              ref.set(stepDetail.providerTypeAbbreviation ?? '', p)
            }}
          >
            {({ setFieldValue }) => {
              useEffect(() => {
                setSetFieldValueRef(() => setFieldValue)
              }, [setFieldValue])

              return (
                <Form
                  onBlur={onBlur}
                  onInputCapture={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                >
                  <div>
                    {renderClaimButtons(stepDetail.providerTypeAbbreviation ?? '')}
                  </div>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell align="right">
                          <span>Date of Service</span>
                        </TableCell>
                        <TableCell align="right">
                          <span>Group #</span>
                        </TableCell>
                        <TableCell>
                          <span>Status</span>
                        </TableCell>
                        <TableCell>
                          <span>Place of Service</span>
                        </TableCell>
                        <TableCell>
                          <span>CPT</span>
                        </TableCell>
                        <TableCell colSpan={4}>
                          <span>Modifiers</span>
                        </TableCell>
                        <TableCell>
                          <span>Diagnosis Pointer</span>
                        </TableCell>
                        <TableCell align="right">
                          <span>Charges</span>
                        </TableCell>
                        <TableCell align="right">
                          <span>Usual and Customary</span>
                        </TableCell>
                        <TableCell>
                          <span></span>
                        </TableCell>
                        <TableCell align="right">
                          <span>Qty</span>
                        </TableCell>
                        <TableCell>
                          <span>NPI</span>
                        </TableCell>
                        <TableCell align="right">
                          <span>Client Assistance Field</span>
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {val &&
                        val.lineItems.map(
                          (lineItem: ICombined1500ComponentLineItem, index: number) => {
                            return (
                              <TableRow
                                key={lineItem.id || index}
                                className={lineItem.isExcluded ? 'strikeOut' : ''}
                              >
                                <TableCell align="right">
                                  <span>
                                    {lineItem.dateOfService &&
                                      Moment(lineItem.dateOfService).format('MM/DD/YYYY')}
                                  </span>
                                </TableCell>
                                <TableCell align="right">
                                  {lineItem.claimGroupNumber}
                                </TableCell>
                                <TableCell>{lineItem.status}</TableCell>
                                <TableCell>{lineItem.placeOfService}</TableCell>
                                <TableCell>{lineItem.cpt}</TableCell>
                                <TableCell width={'80px'}>
                                  <FullWidthField
                                    variant="standard"
                                    name={`lineItems[${index}].modifier1`}
                                    id={`lineItems[${index}].modifier1`}
                                    disabled={val && val.lineItems[index].isExcluded}
                                  />
                                </TableCell>
                                <TableCell width={'80px'}>
                                  <FullWidthField
                                    variant="standard"
                                    name={`lineItems[${index}].modifier2`}
                                    id={`lineItems[${index}].modifier2`}
                                    disabled={val && val.lineItems[index].isExcluded}
                                  />
                                </TableCell>
                                <TableCell width={'80px'}>
                                  <FullWidthField
                                    variant="standard"
                                    name={`lineItems[${index}].modifier3`}
                                    id={`lineItems[${index}].modifier3`}
                                    disabled={val && val.lineItems[index].isExcluded}
                                  />
                                </TableCell>
                                <TableCell width={'80px'}>
                                  <FullWidthField
                                    variant="standard"
                                    name={`lineItems[${index}].modifier4`}
                                    id={`lineItems[${index}].modifier4`}
                                    disabled={val && val.lineItems[index].isExcluded}
                                  />
                                </TableCell>
                                <TableCell>
                                  <FullWidthField
                                    variant="standard"
                                    name={`lineItems[${index}].diagnosisPointer`}
                                    id={`lineItems[${index}].diagnosisPointer`}
                                    disabled={val && val.lineItems[index].isExcluded}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(lineItem.charges.toString())}
                                </TableCell>
                                <TableCell align="right">
                                  <FullWidthField
                                    variant="standard"
                                    inputProps={{ style: { textAlign: 'right' } }}
                                    name={`lineItems[${index}].usualAndCustomary`}
                                    id={`lineItems[${index}].usualAndCustomary`}
                                    disabled={val && val.lineItems[index].isExcluded}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Tooltip title="Choose Historical Billed Out Amount">
                                    <LocalAtmRounded
                                      fontSize="large"
                                      color={
                                        val && val.lineItems[index].isExcluded
                                          ? 'disabled'
                                          : 'primary'
                                      }
                                      style={{
                                        cursor:
                                          val && val.lineItems[index].isExcluded
                                            ? 'default'
                                            : 'pointer',
                                      }}
                                      onClick={() => {
                                        if (val && !val.lineItems[index].isExcluded) {
                                          handleOpenModal(index)
                                        }
                                      }}
                                    />
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="right">{lineItem.quantity}</TableCell>
                                <TableCell>
                                  <FullWidthField
                                    variant="standard"
                                    name={`lineItems[${index}].npi`}
                                    id={`lineItems[${index}].npi`}
                                    disabled={val && val.lineItems[index].isExcluded}
                                  />
                                </TableCell>
                                <TableCell>
                                  <FullWidthField
                                    variant="standard"
                                    name={`lineItems[${index}].jRendering`}
                                    id={`lineItems[${index}].jRendering`}
                                    disabled={val && val.lineItems[index].isExcluded}
                                  />
                                </TableCell>
                                <TableCell>
                                  {lineItem.isExcluded ? (
                                    <PlaylistAdd
                                      onClick={() => {
                                        setFieldValue(
                                          `lineItems.${index}.isExcluded`,
                                          false
                                        )
                                        setTimeout(() => {
                                          updateModel()
                                        })
                                      }}
                                      cursor="pointer"
                                    />
                                  ) : (
                                    <PlaylistRemove
                                      onClick={() => {
                                        setFieldValue(
                                          `lineItems.${index}.isExcluded`,
                                          true
                                        )
                                        setTimeout(() => {
                                          updateModel()
                                        })
                                      }}
                                      cursor="pointer"
                                      color="error"
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            )
                          }
                        )}
                    </TableBody>
                  </Table>
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginTop: 8,
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleCopyChargesToUC()}
                    >
                      Copy Charges to U&C
                    </Button>
                    &nbsp;&nbsp;
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!hasBlankUAndC()}
                      onClick={() => zeroOutRemaining()}
                    >
                      Zero Out Remaining
                    </Button>
                    &nbsp;&nbsp;
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!hasBlankUAndC()}
                      onClick={() => excludeRemaining()}
                    >
                      Exclude Remaining
                    </Button>
                    &nbsp;&nbsp;
                  </div>
                </Form>
              )
            }}
          </Formik>
        ) : (
          <></>
        )}
        {selectedLineItem && (
          <BillOutHistoryModal
            close={handleCloseModal}
            isOpen={isBillOutHistoryModalOpen}
            cptCode={selectedLineItem.cpt ?? ''}
            providerTypeId={selectedLineItem.providerTypeId}
            providerGroupId={selectedLineItem.providerId}
            selectedCms1500Id={wizardState.selectedCms1500Id ?? ''}
            summary={summary}
            onCopy={(amount) => {
              if (selectedIndex !== undefined) {
                setFieldValueRef(
                  `lineItems[${selectedIndex}].usualAndCustomary`,
                  amount.toFixed(2)
                )
                setTimeout(() => {
                  updateModel()
                })
              }
            }}
          />
        )}
      </div>
    </CardWithNoTitle>
  )
}

function getFinalStepContent(
  summary: Combined1500GeneralSummary | null,
  pdfContent: any
) {
  return (
    <CardWithNoTitle>
      {renderHeader(summary)}
      <iframe
        src={pdfContent}
        style={{ width: '100%', height: '600px', marginTop: '22px' }}
      ></iframe>
    </CardWithNoTitle>
  )
}

function getStep1Content(
  summary: Combined1500GeneralSummary | null,
  wizardState: IWizardState,
  setWizardState: any,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  updateModelRef?: React.MutableRefObject<(() => void) | null>,
  formRefObj?: React.MutableRefObject<any | null>
) {
  const [activeSelectedDxCode, setActiveSelectedDxCode] = useState<string | null>(null)
  const [activeAvailableDxCode, setActiveAvailableDxCode] = useState<string | null>(null)

  const availableDxCodes = () =>
    summary?.dxCodes?.filter((c) => !wizardState.selectedDxCodes.includes(c)) ?? []

  const selectAvailableDxCode = (dxCode: string) => () => {
    setActiveAvailableDxCode(dxCode)
  }

  const selectSelectedDxCode = (dxCode: string) => () => {
    setActiveSelectedDxCode(dxCode)
  }

  const addDxCode = () => {
    activeAvailableDxCode &&
      setWizardState({
        ...wizardState,
        selectedDxCodes: wizardState.selectedDxCodes.concat(activeAvailableDxCode ?? ''),
      })
    setActiveAvailableDxCode(null)
  }

  const removeDxCode = () => {
    activeSelectedDxCode &&
      setWizardState({
        ...wizardState,
        selectedDxCodes: wizardState.selectedDxCodes.filter(
          (x) => x != activeSelectedDxCode
        ),
      })
    setActiveAvailableDxCode(null)
  }

  const isGridDataEmpty = () => {
    return summary?.claims?.length == 0
  }

  const handleRowClick = async (_: any, cms1500Id: string | null) => {
    setIsLoading(true)
    try {
      await GetCombined1500ClaimFieldSummaryAsync(cms1500Id ?? '').then((response) => {
        setWizardState({
          ...wizardState,
          selectedCms1500Id: cms1500Id,
          dateOfInjury: response.dateOfInjury,
          box14Qualifier: response.box14Qualifier,
          otherDate: response.otherDate,
          box15Qualifier: response.box15Qualifier,
        })
      })
    } catch (error) {
      console.error('Error processing action:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const mapGridData = () => {
    if (isGridDataEmpty()) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={7} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    } else {
      return (
        <TableBody style={{ width: '100%' }}>
          {summary?.claims
            ?.sort(
              (a, b) =>
                new Date(a.dateOfService).getTime() - new Date(b.dateOfService).getTime()
            )
            .slice(
              wizardState.claimsGridState.firstRecordIndex,
              wizardState.claimsGridState.lastRecordIndex + 1
            )
            ?.map((claim, _) => {
              const isItemSelected = wizardState.selectedCms1500Id == claim.cms1500Id
              return (
                <TableRow
                  hover
                  onClick={(event) => handleRowClick(event, claim.cms1500Id)}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={claim.cms1500Id}
                  selected={isItemSelected}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox color="primary" checked={isItemSelected} />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="text"
                      color="primary"
                      sx={{ userSelect: 'text' }}
                      style={{
                        textTransform: 'none',
                        padding: 0,
                        minWidth: 0,
                        background: 'none',
                        border: 'none',
                        boxShadow: 'none',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontWeight: 'normal',
                        fontSize: 'inherit',
                        lineHeight: 'inherit',
                      }}
                      onMouseOver={(e) => {
                        ;(e.currentTarget as HTMLElement).style.textDecoration =
                          'underline'
                      }}
                      onMouseOut={(e) => {
                        ;(e.currentTarget as HTMLElement).style.textDecoration =
                          'underline'
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        window.open(
                          `/claim/${claim.cms1500Id}/episodeOfCare/${summary.quoteDetail?.episodeOfCareId}/view`,
                          '_blank'
                        )
                      }}
                    >
                      {claim.eocGroupNumber}
                    </Button>
                  </TableCell>
                  <TableCell style={{ padding: '0', width: '1px', whiteSpace: 'nowrap' }}>
                    {claim.isConsumedClaim != undefined &&
                    claim.isConsumedClaim == true ? (
                      <Tooltip title={`Consumed Claim`}>
                        <span
                          style={{
                            fontSize: 'small',
                            verticalAlign: 'middle',
                            display: 'inline-flex',
                            alignItems: 'center',
                          }}
                        >
                          🐠&nbsp;&nbsp;
                        </span>
                      </Tooltip>
                    ) : (
                      <></>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {claim.dateOfService &&
                    new Date(claim.dateOfService!).valueOf() >
                      new Date('1900-01-17T03:24:00').valueOf()
                      ? Moment(claim.dateOfService).format('MM/DD/YYYY')
                      : ''}
                  </TableCell>
                  <TableCell>{claim.claimType}</TableCell>
                  <TableCell>{claim.providerGroup}</TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>
                    {claim.providerLocation}
                  </TableCell>
                  <TableCell align="right">
                    {claim.totalClaimCharges != undefined
                      ? formatCurrency(claim.totalClaimCharges.toString())
                      : ''}
                  </TableCell>
                </TableRow>
              )
            })}
        </TableBody>
      )
    }
  }

  const renderClaimsGrid = () => {
    const paginationInfo = () => {
      if (!isGridDataEmpty()) {
        return (
          <div style={{ paddingBottom: '20px' }} className="pagination">
            <Typography variant="caption" gutterBottom>
              Showing {wizardState.claimsGridState.firstRecordIndex + 1} to{' '}
              {wizardState.claimsGridState.lastRecordIndex + 1} of{' '}
              {wizardState.claimsGridState.totalItems} entries
            </Typography>
          </div>
        )
      } else {
        return <></>
      }
    }

    const handlePageChange = (event: any) => {
      let page = event.selected + 1
      let firstRecordIndex = (page - 1) * wizardState.claimsGridState.pageSize
      let lastRecordIndex = Math.min(
        (summary?.claims?.length ?? 0) - 1,
        firstRecordIndex - 1 + wizardState.claimsGridState.pageSize
      )
      setWizardState({
        ...wizardState,
        claimsGridState: {
          ...wizardState.claimsGridState,
          page: page,
          firstRecordIndex: firstRecordIndex,
          lastRecordIndex: lastRecordIndex,
        },
      })
    }

    return (
      <div style={{ width: '100%' }}>
        <div style={{ width: '100%', marginTop: '22px', marginBottom: '11px' }}>
          <AccordionSummary>
            <Grid container justifyContent="space-between" style={{ paddingTop: '5px' }}>
              <Typography
                display="inline"
                color="textSecondary"
                variant="body2"
                component="div"
              >
                SELECT CLAIM
              </Typography>
              <Typography
                display="inline"
                color="textPrimary"
                variant="subtitle1"
                align="right"
                component="div"
              >
                <span></span>
              </Typography>
            </Grid>
          </AccordionSummary>
        </div>

        <Grid container item md={12} style={{ minWidth: '750px', width: '100%' }}>
          <Table aria-label="simple table" style={{ padding: '20' }}>
            <TableHead>
              <TableRow className="gridPadding">
                <TableCell></TableCell>
                <TableCell>EOC/Legacy</TableCell>
                <TableCell></TableCell>
                <TableCell align="right">Date of Service</TableCell>
                <TableCell>Provider Type</TableCell>
                <TableCell>Provider Group</TableCell>
                <TableCell>Provider Location</TableCell>
                <TableCell align="right">Total Charge</TableCell>
              </TableRow>
            </TableHead>

            {mapGridData()}
          </Table>
        </Grid>
        <div className="pagination-row">
          {paginationInfo()}
          <ReactPaginate
            previousLabel={'<'}
            nextLabel={'>'}
            onPageChange={handlePageChange}
            pageCount={wizardState.claimsGridState.pageCount}
            containerClassName={'pagination'}
            activeClassName={'active'}
            forcePage={wizardState.claimsGridState.page - 1}
            renderOnZeroPageCount={undefined}
          />
        </div>
      </div>
    )
  }

  let updateModel = () => {
    const formRef = formRefObj?.current
    if (!formRef) {
      return
    }

    setWizardState({
      ...wizardState,
      dateOfInjury: formRef.values.dateOfInjury,
      box14Qualifier: formRef.values.box14Qualifier,
      otherDate: formRef.values.otherDate,
      box15Qualifier: formRef.values.box15Qualifier,
      referringDoctor: formRef.values.referringDoctor,
      referringDoctorNpi: formRef.values.referringDoctorNpi,
      box30: formRef.values.box30,
    })
  }

  if (updateModelRef) {
    updateModelRef.current = updateModel
  }

  const onBlur: any = () => {
    updateModel()
  }

  const renderDxCodePicker = () => {
    return (
      <div style={{ width: '100%' }}>
        <div style={{ width: '100%', marginTop: '22px', marginBottom: '11px' }}>
          <AccordionSummary>
            <Grid container justifyContent="space-between" style={{ paddingTop: '5px' }}>
              <Typography
                display="inline"
                color="textSecondary"
                variant="body2"
                component="div"
              >
                SELECT DX CODES
              </Typography>
              <Typography
                display="inline"
                color="textPrimary"
                variant="subtitle1"
                align="right"
                component="div"
              >
                <span></span>
              </Typography>
            </Grid>
          </AccordionSummary>
        </div>
        <div style={{ width: '500px', display: 'inline-block', verticalAlign: 'top' }}>
          <Grid container direction="row">
            <Grid item container direction="row">
              <Grid item xs={5}>
                <div>Available</div>
              </Grid>
              <Grid item xs={2} />
              <Grid item xs={5}>
                <div>Selected</div>
              </Grid>
            </Grid>

            <Grid container direction="row" justifyContent="center">
              <Grid item xs={5}>
                <Paper elevation={0} style={{ border: '#CCCCCC 1px solid' }}>
                  <List
                    dense={true}
                    disablePadding={true}
                    style={{ height: '300px', overflowY: 'auto' }}
                  >
                    {availableDxCodes ? (
                      availableDxCodes().map((x: string) => (
                        <ListItem
                          key={x}
                          button
                          onClick={selectAvailableDxCode(x)}
                          selected={x != undefined && x == activeAvailableDxCode}
                        >
                          {x}
                        </ListItem>
                      ))
                    ) : (
                      <></>
                    )}
                  </List>
                </Paper>
              </Grid>
              <Grid item xs={2}>
                <Grid
                  container
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  style={{ height: '100%' }}
                  spacing={2}
                >
                  <Grid item>
                    <Button variant="contained" color="primary" onClick={addDxCode}>
                      &gt;
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button variant="contained" color="primary" onClick={removeDxCode}>
                      &lt;
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={5}>
                <Paper elevation={0} style={{ border: '#CCCCCC 1px solid' }}>
                  <List
                    dense={true}
                    disablePadding={true}
                    style={{ height: '300px', overflowY: 'auto' }}
                  >
                    {wizardState.selectedDxCodes.map((x: string) => (
                      <ListItem
                        key={x}
                        button
                        onClick={selectSelectedDxCode(x)}
                        selected={x != undefined && x == activeSelectedDxCode}
                      >
                        {x}
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </div>
        <div
          style={{
            width: '300px',
            display: 'inline-block',
            marginLeft: '36px',
            marginTop: '24px',
          }}
        >
          <Formik
            initialValues={wizardState}
            onSubmit={() => alert('submit')}
            enableReinitialize={true}
            innerRef={(p: any) => {
              if (formRefObj) {
                formRefObj.current = p
              }
            }}
          >
            {({}) => {
              return (
                <Form onBlur={onBlur}>
                  <Grid
                    container
                    direction="column"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Grid item>
                      <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        spacing={2}
                      >
                        <Grid item xs={6}>
                          <DatePickerField
                            fullWidth
                            variant="standard"
                            name={'dateOfInjury'}
                            inputId={'dateOfInjury'}
                            label={'Date of Injury'}
                            shrink={true}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FullWidthField
                            variant="standard"
                            name={'box14Qualifier'}
                            id={'box14Qualifier'}
                            label={'Box 14 Qualifier'}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item>
                      <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        spacing={2}
                      >
                        <Grid item xs={6}>
                          <DatePickerField
                            fullWidth
                            name={'otherDate'}
                            inputId={'otherDate'}
                            label={'Other Date'}
                            variant="standard"
                            shrink={true}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FullWidthField
                            variant="standard"
                            name={'box15Qualifier'}
                            id={'box15Qualifier'}
                            label={'Box 15 Qualifier'}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item>
                      <FullWidthField
                        variant="standard"
                        name={'referringDoctor'}
                        id={'referringDoctor'}
                        label={'Referring Doctor'}
                      />
                    </Grid>
                    <Grid item>
                      <FullWidthField
                        variant="standard"
                        name={'referringDoctorNpi'}
                        id={'referringDoctorNpi'}
                        label={'Referring Doctor NPI'}
                      />
                    </Grid>
                    <Grid item>
                      <FullWidthField
                        type="number"
                        variant="standard"
                        name={'box30'}
                        id={'box30'}
                        label={'Box 30'}
                      />
                    </Grid>
                  </Grid>
                </Form>
              )
            }}
          </Formik>
        </div>
      </div>
    )
  }

  return (
    <CardWithNoTitle>
      {renderHeader(summary)}

      {renderClaimsGrid()}

      {renderDxCodePicker()}
    </CardWithNoTitle>
  )
}

export default function CreateCombined1500(props: any) {
  const [activeStep, setActiveStep] = useState(0)
  const [steps, setSteps] = useState<StepDetail[]>([
    {
      name: 'General Information',
      providerTypeAbbreviation: undefined,
    },
    {
      name: 'Final Review',
      providerTypeAbbreviation: undefined,
    },
  ])
  const [isValid, setIsValid] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isFinished, setIsFinished] = useState(false)
  const [completedSteps, setCompletedSteps] = useState<number[]>([])
  const [summary, setSummary] = useState<Combined1500GeneralSummary | null>(null)
  const [wizardState, setWizardState] = useState<IWizardState>(new WizardState())
  const [pdfContent, setPdfContent] = useState<string | null>(null)
  const step1UpdateModelRef = useRef<(() => void) | null>(null)
  const step1FormRef = useRef<any | null>(null)
  const pathSegments = window.location.pathname.split('/')
  const quoteId = pathSegments[3]
  const params = queryString.parse(location.search)
  const inboxItemId: string = params['inboxItemId']!.toString()
  const actionId: string = params['actionId']!.toString()

  const buildRequest = () => {
    const lineItems = wizardState.combined1500ComponentDetails.flatMap((x) => x.lineItems)
    lineItems.forEach((x) => {
      let sortIndex = wizardState.lineItemSort.findIndex((y) => y === x.id)
      x.lineNumber = sortIndex + 1
    })
    const request: Combined1500Request = {
      lineItems: lineItems,
      dateOfInjury: wizardState.dateOfInjury,
      box14Qualifier: wizardState.box14Qualifier,
      otherDate: wizardState.otherDate,
      box15Qualifier: wizardState.box15Qualifier,
      referringDoctor: wizardState.referringDoctor,
      referringDoctorNpi: wizardState.referringDoctorNpi,
      selectedCms1500Id: wizardState.selectedCms1500Id ?? '',
      selectedDxCodes: wizardState.selectedDxCodes,
      box30: wizardState.box30,
    }

    return request
  }

  const fetchPdfContent = async () => {
    setIsLoading(true)
    try {
      const request = buildRequest()

      const pdfResponse = await previewCombined1500(request)
      const url = createPdfObjUrl(pdfResponse)

      setPdfContent(url)
    } catch (_error) {}
    setIsLoading(false)
  }

  const fetchLineItems = async (providerTypeAbbreviation: string) => {
    if (
      wizardState.combined1500ComponentDetails.filter(
        (x) => x.providerTypeAbbreviation == providerTypeAbbreviation
      ).length
    ) {
      return
    }

    setIsLoading(true)
    try {
      const lineItems = await GetCombined1500ComponentDetail(
        quoteId,
        providerTypeAbbreviation
      )

      if (lineItems.lineItems) {
        lineItems.lineItems = lineItems.lineItems.map((item: any) => ({
          ...item,
          diagnosisPointer: item.diagnosisPointer ?? '',
          usualAndCustomary: item.usualAndCustomary ?? '',
        }))
      }

      let buttonResults = await GetClaimButtonsWithSummary(
        quoteId,
        providerTypeAbbreviation
      )

      let newClaimButtonsByType = { ...wizardState.claimButtonsByType }
      if (buttonResults.claimButtons) {
        newClaimButtonsByType[providerTypeAbbreviation] = buttonResults.claimButtons.map(
          (item: any) => ({
            ...item,
            diagnosisPointer: item.diagnosisPointer ?? '',
            usualAndCustomary: item.usualAndCustomary ?? '',
          })
        )
      }
      setWizardState({
        ...wizardState,
        combined1500ComponentDetails: [
          ...wizardState.combined1500ComponentDetails,
          lineItems,
        ],
        claimButtonsByType: newClaimButtonsByType,
      })
    } catch (_error) {}
    setIsLoading(false)
  }

  const fetchClaimCounts = async () => {
    try {
      const response = await GetCombined1500GeneralSummary(quoteId)
      const { pcClaimCount, tcClaimCount, anClaimCount, ptClaimCount, miClaimCount } =
        response

      let page = 1
      let firstRecordIndex = (page - 1) * wizardState.claimsGridState.pageSize
      let lastRecordIndex = Math.min(
        (response.claims?.length ?? 0) - 1,
        firstRecordIndex + wizardState.claimsGridState.pageSize - 1
      )
      let totalItems = response.claims?.length ?? 0
      setWizardState({
        ...wizardState,
        claimsGridState: {
          ...wizardState.claimsGridState,
          page: page,
          firstRecordIndex: firstRecordIndex,
          lastRecordIndex: lastRecordIndex,
          totalItems: totalItems,
          pageCount: Math.ceil(totalItems / wizardState.claimsGridState.pageSize),
        },
      })

      setSummary(response)

      const newSteps: StepDetail[] = [
        {
          name: 'General Information',
          providerTypeAbbreviation: undefined,
        },
      ]

      if (pcClaimCount > 0)
        newSteps.push({
          name: 'Professional Component',
          providerTypeAbbreviation: 'PC',
        })
      if (tcClaimCount > 0)
        newSteps.push({
          name: 'Technical Component',
          providerTypeAbbreviation: 'TC',
        })
      if (anClaimCount > 0)
        newSteps.push({
          name: 'Anesthesiologist',
          providerTypeAbbreviation: 'AN',
        })
      if (miClaimCount > 0)
        newSteps.push({
          name: 'Miscellaneous',
          providerTypeAbbreviation: 'MI',
        })
      if (ptClaimCount > 0)
        newSteps.push({
          name: 'Physical Therapy',
          providerTypeAbbreviation: 'PT',
        })

      newSteps.push({
        name: 'Line Review',
        providerTypeAbbreviation: undefined,
      })
      newSteps.push({
        name: 'Finish',
        providerTypeAbbreviation: undefined,
      })

      setSteps(newSteps)
    } catch (_error) {}

    setIsLoading(false)
  }

  useEffect(() => {
    fetchClaimCounts()
  }, [])

  useEffect(() => {
    const validate = async () => {
      let activeStepDetail = steps[activeStep]
      let schema = schemas[activeStep]

      if (schema) {
        if (activeStepDetail.providerTypeAbbreviation) {
          let componentDetail = wizardState.combined1500ComponentDetails.find(
            (x) =>
              x.providerTypeAbbreviation === activeStepDetail.providerTypeAbbreviation
          )

          if (componentDetail) {
            const detailToValidate = {
              ...componentDetail,
              lineItems: componentDetail.lineItems.filter(
                (item: any) => !item.isExcluded
              ),
            }

            schema
              .validate(detailToValidate)
              .then(() => {
                setIsValid(true)
              })
              .catch((_err: any) => {
                setIsValid(false)
              })
          } else {
            setIsValid(false)
          }
        } else {
          schema
            .validate(wizardState)
            .then(() => {
              setIsValid(true)
            })
            .catch((_err: any) => {
              setIsValid(false)
            })
        }
      } else {
        setIsValid(true)
      }
    }

    validate().catch(console.error)
  }, [wizardState, activeStep])

  const handleNext = async () => {
    if (activeStep === 0 && step1UpdateModelRef.current) {
      step1UpdateModelRef.current()
    }

    let activeStepDetail = steps[activeStep]
    let formRef = ref.get(activeStepDetail.providerTypeAbbreviation ?? '')

    if (formRef) {
      let componentDetails: ICombined1500ComponentDetail[] = [
        ...wizardState.combined1500ComponentDetails,
      ]
      let arrayIndex = componentDetails.findIndex(
        (x) => x.providerTypeAbbreviation == activeStepDetail.providerTypeAbbreviation
      )

      // Preserve invoiceUsualAndCustomaryTotal from the existing component detail
      let existingDetail = componentDetails[arrayIndex]
      let updatedComponentDetail = {
        ...formRef.values,
        invoiceUsualAndCustomaryTotal: existingDetail?.invoiceUsualAndCustomaryTotal ?? 0,
      }
      componentDetails[arrayIndex] = updatedComponentDetail

      const valuesToValidate = {
        ...updatedComponentDetail,
        lineItems: updatedComponentDetail.lineItems.filter(
          (item: any) => !item.isExcluded
        ),
      }

      const schema = componentSchema
      schema
        .validate(valuesToValidate)
        .then(() => {
          const updatedWizardState = {
            ...wizardState,
            combined1500ComponentDetails: componentDetails,
          }
          setWizardState(updatedWizardState)
          goNext()
        })
        .catch((_err: any) => {
          formRef.validateForm().then((errors: any) => {
            formRef.setTouched({ ...errors })
          })
        })
      return
    }

    if (activeStep === steps.length - 1) {
      setIsLoading(true)

      let data = new ObjectiveProcessAction()
      const request = buildRequest()
      data.combined1500Request = request

      try {
        await processAction(data, actionId, inboxItemId)
        props.location?.state?.from
          ? props.history.push(props.location.state.from)
          : history.back()
      } catch (error) {
        console.error('Error processing action:', error)
      } finally {
        setIsLoading(false)
      }
    } else {
      goNext()
    }
  }

  const goNext = async () => {
    const nextStep = activeStep + 1
    if (nextStep === steps.length) {
      setIsFinished(true)
    } else {
      setActiveStep(nextStep)
      let sd = steps[nextStep]
      sd.providerTypeAbbreviation &&
        (await fetchLineItems(sd.providerTypeAbbreviation ?? ''))
      if (nextStep === steps.length - 1) {
        await fetchPdfContent()
      }
      if (nextStep === steps.length - 2) {
        initializeLineSort()
      }
    }

    setCompletedSteps((prevCompletedSteps) => [...prevCompletedSteps, activeStep])
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const initializeLineSort = () => {
    let lineItemIds: string[] = wizardState.combined1500ComponentDetails.flatMap((x) =>
      x.lineItems.filter((y) => !y.isExcluded).map((y) => y.id)
    )

    if (wizardState.lineItemSort.length === 0) {
      setWizardState({
        ...wizardState,
        lineItemSort: lineItemIds,
      })
    } else {
      let filteredLineItemSort = wizardState.lineItemSort.filter((x: string) =>
        lineItemIds.includes(x)
      )
      let missignLineItemSort = lineItemIds.filter(
        (x) => !wizardState.lineItemSort.includes(x)
      )
      filteredLineItemSort = filteredLineItemSort.concat(missignLineItemSort)
      setWizardState({
        ...wizardState,
        lineItemSort: filteredLineItemSort,
      })
    }
  }

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100vh',
      }}
    >
      <Helmet>
        <title>Combined 1500</title>
      </Helmet>
      {isLoading ? (
        <div
          style={{
            position: 'fixed',
            display: 'flex',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            cursor: 'pointer',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 800,
          }}
        >
          <CircularProgress size={100} />
        </div>
      ) : (
        <></>
      )}

      <Stepper activeStep={activeStep} alternativeLabel sx={{ pt: 2, pb: 2 }}>
        {steps.map((stepDetail, index) => (
          <Step key={stepDetail.name} completed={completedSteps.includes(index)}>
            <StepLabel>{stepDetail.name}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <div
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isFinished ? (
          <div>
            <Typography sx={{ mt: 1, mb: 1 }} component="div">
              All steps completed
            </Typography>
          </div>
        ) : (
          <div>
            <Typography sx={{ mt: 1, mb: 1 }} component="div">
              {getStepContent(
                activeStep,
                summary,
                wizardState,
                setWizardState,
                setIsLoading,
                steps,
                pdfContent,
                step1UpdateModelRef,
                step1FormRef
              )}
            </Typography>
          </div>
        )}
      </div>
      <div className={'buttonContainer'}>
        <div style={{ display: 'flex', padding: 16 }}>
          <Button
            onClick={() =>
              props.location?.state?.from
                ? props.history.push(props.location.state.from)
                : history.back()
            }
            variant="contained"
            sx={{ ml: 1 }}
          >
            Cancel
          </Button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 16 }}>
          <Button
            disabled={activeStep === 0 || isFinished}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={
              isFinished ||
              !isValid ||
              (activeStep === steps.length - 3 &&
                wizardState.combined1500ComponentDetails.filter(
                  (x) => x.lineItems.filter((y) => !y.isExcluded).length > 0
                ).length == 0)
            }
          >
            {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  )
}
